import type { FunctionComponent } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";

import { EntityLink } from "scmp-app/components/entity-link";
import { getResponsiveProps } from "scmp-app/components/header/features/helpers";
import type { HeaderFeatureCommonProps } from "scmp-app/components/header/features/types";
import type { sectionEntityLinkHeaderFeaturesSectionQuery } from "scmp-app/queries/__generated__/sectionEntityLinkHeaderFeaturesSectionQuery.graphql";
import type { sectionEntityLinkParentHeaderFeaturesSectionQuery } from "scmp-app/queries/__generated__/sectionEntityLinkParentHeaderFeaturesSectionQuery.graphql";

import { Container } from "./styles";

export type entityLinkNameSize = "large" | "small";

export type HeaderFeaturesSectionEntityLinkProps = HeaderFeatureCommonProps & {
  isSubSection?: boolean;
  sectionId?: string | string[] | undefined;
  size?: entityLinkNameSize;
};

export const HeaderFeaturesSectionEntityLink: FunctionComponent<
  HeaderFeaturesSectionEntityLinkProps
> = ({ isSubSection, sectionId, size = "small", ...props }) => {
  const data = useLazyLoadQuery<sectionEntityLinkHeaderFeaturesSectionQuery>(
    graphql`
      query sectionEntityLinkHeaderFeaturesSectionQuery($entityId: String!) {
        section(filter: { entityId: $entityId }) {
          name
          ...entityLink
          parentSection
        }
      }
    `,
    { entityId: Array.isArray(sectionId) ? sectionId[0] : (sectionId ?? "") },
  );

  const parentSectionUuid = data.section?.parentSection;
  const parentSectionData = useLazyLoadQuery<sectionEntityLinkParentHeaderFeaturesSectionQuery>(
    graphql`
      query sectionEntityLinkParentHeaderFeaturesSectionQuery($entityUuid: String!) {
        section(filter: { entityUuid: $entityUuid }) {
          name
          ...entityLink
        }
      }
    `,
    { entityUuid: parentSectionUuid ?? "" },
  );

  if (!data) return null;

  return (
    <Container $size={size} {...getResponsiveProps(props)}>
      {isSubSection ? (
        <EntityLink reference={parentSectionData.section}>
          {parentSectionData.section?.name}
        </EntityLink>
      ) : (
        <EntityLink reference={data.section}>{data.section?.name}</EntityLink>
      )}
    </Container>
  );
};

HeaderFeaturesSectionEntityLink.displayName = "HeaderFeaturesSectionEntityLink";
